import { put, call, select } from 'redux-saga/effects';
import get from 'lodash/get';

import { browserHistory } from 'react-router';
import { DEV_ENV } from '../../constants/env';
import createConsts from './consts';
import { LOGIN, PAPERWORK } from '../../constants/routes';
import { checkIsPaperworkRoute } from '../paperwork';
import { selectAuthLoggedInStatus } from '../../redux/auth/selectors';
import {
  PAPERWORK_TOKEN,
  AUTHENTICATION_TOKEN,
} from '../../constants/storageKeys';

// Hardcode logout succeed const as it isn't available at this point.
const LOGOUT_SUCCEED = 'auth/logout/SUCCEED';
const LOGIN_API_URI = 'users/sessions';
const ACCEPT_INVITATION_URI = 'invite_confirmations/';
const UNAUTHORIZED = 401;
const REQUEST_TIMEOUT = 408;
const BAD_GATEWAY = 502;
const GATEWAY_TIMEOUT = 504;

/*
 * The worker that intercepts API HTTP requests. The actions MUST follow such naming convention:
 * const actions = {
 *   succeed,
 *   rejected,
 *   errorEncountered,
 * };
 * The actions itself must be a plain object, contains action creators for
 * `succeed` (>= 200 and < 400 HTTP responses),
 * `rejected` (>= 400 and < 500)
 * and `errorEncountered` actions (for any errors, both server (>= 500) and client) accordingly
 * */

function* manageResponse(actions, api, payload) {
  const { succeed, rejected, errorEncountered, addMeta } = actions;
  try {
    const response = yield call(api.send, payload);
    const status = get(response, 'status');
    const responseBody = get(response, 'data');
    const getEntity = get.bind(null, responseBody);
    const c = ['data', 'meta', 'error'].reduce((acc, x) => {
      acc[x] = getEntity(x); // eslint-disable-line no-param-reassign
      return acc;
    }, {});

    // fallback for file responses
    if (!c.data && !c.error) {
      c.blob = { blob: responseBody };
    }

    if (status >= 200 && status < 400) {
      yield put(succeed(c.data || c.blob, c.meta));

      if (c.meta) {
        yield put(addMeta(c.meta));
      }
    } else if (status >= 400 && status < 500) {
      if (DEV_ENV) console.warn(c.error);

      const errorData = {
        ...get(c, 'error', {}),
        code: get(c, 'data.error.code', null),
        description: get(c, 'data.error.description', null),
        meta: get(c, 'data.meta', null),
        status,
      };

      yield put(rejected({ error: errorData }, c.meta));

      if (status === UNAUTHORIZED) {
        const communityId = response.config.url.split('/')[5];
        const COMMUNITY_PREVIEW = `api/communities/${communityId}/member_invitations`;
        const exceptions = [
          LOGIN_API_URI,
          ACCEPT_INVITATION_URI,
          COMMUNITY_PREVIEW,
        ];

        const exceptionUrl = exceptions.find(
          key => response.config.url.indexOf(key) > -1,
        );

        if (!exceptionUrl) {
          const isLoggedIn = yield select(selectAuthLoggedInStatus);
          if (!isLoggedIn) {
            return;
          }

          const { isPaperworkRoute, route } = checkIsPaperworkRoute();

          const requestAuthHeader = get(
            response,
            'config.headers.Authorization',
            '',
          );

          // If no Authorization header was sent, this was an unauthenticated request.
          // Don't clear localStorage — a tokenless tab must not wipe another tab's session.
          if (!requestAuthHeader) {
            return;
          }

          // Extract the raw token value from the Authorization header.
          // Handles: "Token token=<value>" and "Bearer token=<value>, token_type=..."
          const requestTokenMatch = requestAuthHeader.match(/token=([^,\s]+)/i);
          const requestToken = requestTokenMatch ? requestTokenMatch[1] : '';

          const currentAuthToken = window.storage.getItem(AUTHENTICATION_TOKEN);
          const currentPaperworkToken = window.storage.getItem(PAPERWORK_TOKEN);
          // If the request was made with a token that no longer matches what is stored
          // (stale in-flight request from this tab or another tab with an old session),
          // do not invalidate the current session.
          if (
            requestToken &&
            (currentAuthToken || currentPaperworkToken) &&
            requestToken !== currentAuthToken &&
            requestToken !== currentPaperworkToken
          ) {
            return;
          }

          // On the paperwork route, only logout when a paperwork API endpoint
          // itself returned 401. Non-paperwork endpoints (e.g. insurance_eligibility)
          // may reject the paperwork-scoped token by design — that must not
          // invalidate the active paperwork session.
          // PAPERWORK constant has a leading slash (frontend route) but axios
          // response.config.url is a relative path without one, so strip it.
          const isPaperworkApiRequest = response.config.url.includes(
            `${PAPERWORK.replace(/^\//, '')}/`,
          );

          if (isPaperworkRoute && !isPaperworkApiRequest) {
            return;
          }

          yield put({ type: LOGOUT_SUCCEED });

          yield call(() =>
            browserHistory.push(isPaperworkRoute ? route : LOGIN),
          );
        }
      }

      if (status === REQUEST_TIMEOUT) {
        yield put({
          type: createConsts('ui', 'error', ['UPDATE']).UPDATE,
          payload: { id: 'errors.longRequest.408' },
        });
      }
    } else if ([BAD_GATEWAY, GATEWAY_TIMEOUT].includes(status)) {
      yield put(rejected({ error: c.error }, c.meta));
      yield put({
        type: createConsts('ui', 'error', ['UPDATE']).UPDATE,
        payload: { id: 'errors.longRequest.502.504' },
      });
    } else {
      throw response;
    }
  } catch (err) {
    if (DEV_ENV) console.error(err);
    yield put(errorEncountered(err));
  }
}

export default manageResponse;



// WEBPACK FOOTER //
// ./src/utils/redux/manage-response.js